import React, { useState, useEffect, useRef } from 'react';
import { listFolders, listObjects, deleteObject, createFolder, uploadFile, deleteFolder, uploadFiles } from '../../services/s3Service';
import s3Service from '../../services/s3Service';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import InstructorFiles from './InstructorFiles';

const FileManagement = ({ user }) => {
  const [fileSection, setFileSection] = useState('general');
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingObject, setDeletingObject] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareableLinks, setShareableLinks] = useState([]);
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [linkCopied, setLinkCopied] = useState(null);
  const [copyingLinkFor, setCopyingLinkFor] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);
  const bulkFileInputRef = useRef(null);

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedFolders([]);
      setSelectedObjects([]);
      const folders = await listFolders();
      // Extract folder names from full paths
      const folderNames = folders.map(folder => folder.replace('/', ''));
      setFolders(folderNames);
    } catch (err) {
      setError('Failed to load folders');
      console.error('Error fetching folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchObjects = async (folderPath = '') => {
    try {
      setLoading(true);
      setError(null);
      setSelectedFolders([]);
      setSelectedObjects([]);
      const objects = await listObjects(folderPath);
      setObjects(objects || []);
      setCurrentFolder(folderPath);
      
      // Update breadcrumb
      if (folderPath) {
        const pathParts = folderPath.split('/').filter(part => part);
        setBreadcrumb(pathParts);
      } else {
        setBreadcrumb([]);
      }
    } catch (err) {
      setError('Failed to load objects');
      console.error('Error fetching objects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteObject = async (objectKey) => {
    if (!window.confirm(`Are you sure you want to delete "${objectKey}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingObject(objectKey);
      await deleteObject(objectKey);
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to delete ${objectKey}`);
      console.error('Error deleting object:', err);
    } finally {
      setDeletingObject(null);
    }
  };

  const navigateToFolder = (folderName) => {
    const newPath = currentFolder ? `${currentFolder}${folderName}/` : `${folderName}/`;
    fetchObjects(newPath);
  };

  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      // Root level
      setCurrentFolder('');
      setBreadcrumb([]);
      fetchFolders();
    } else {
      const newPath = breadcrumb.slice(0, index + 1).join('/') + '/';
      fetchObjects(newPath);
    }
  };

  const goBack = () => {
    if (breadcrumb.length > 0) {
      navigateToBreadcrumb(breadcrumb.length - 2);
    } else {
      setCurrentFolder('');
      setBreadcrumb([]);
      fetchFolders();
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    try {
      setIsCreatingFolder(true);
      const folderPath = currentFolder ? `${currentFolder}${newFolderName}/` : `${newFolderName}/`;
      await createFolder(folderPath);
      
      setIsCreateFolderModalOpen(false);
      setNewFolderName('');
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to create folder: ${err.message}`);
      console.error('Error creating folder:', err);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    try {
      setIsUploadingFile(true);
      await uploadFile(selectedFile, currentFolder);
      
      setIsUploadFileModalOpen(false);
      setSelectedFile(null);
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to upload file: ${err.message}`);
      console.error('Error uploading file:', err);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleDeleteFolder = async (folderKey) => {
    const folderPath = currentFolder ? `${currentFolder}${folderKey}/` : `${folderKey}/`;
    
    if (!window.confirm(`Are you sure you want to delete the folder "${folderKey}" and all its contents? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingFolder(folderKey);
      await deleteFolder(folderPath);
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to delete folder ${folderKey}`);
      console.error('Error deleting folder:', err);
    } finally {
      setDeletingFolder(null);
    }
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleBulkUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    try {
      setIsBulkUploading(true);
      setBulkUploadProgress({ completed: 0, total: selectedFiles.length, percentage: 0, currentFile: '' });
      
      await uploadFiles(selectedFiles, currentFolder, (progress) => {
        setBulkUploadProgress(progress);
      });
      
      setIsBulkUploadModalOpen(false);
      setSelectedFiles([]);
      setBulkUploadProgress(null);
      
      // Reset file input
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to upload files: ${err.message}`);
      console.error('Error uploading files:', err);
    } finally {
      setIsBulkUploading(false);
      setBulkUploadProgress(null);
    }
  };

  const handleFolderSelect = (folderKey) => {
    setSelectedFolders(prev => {
      if (prev.includes(folderKey)) {
        return prev.filter(f => f !== folderKey);
      } else {
        return [...prev, folderKey];
      }
    });
  };

  const handleSelectAllFolders = () => {
    const folderData = getTableData().filter(item => item.isFolder);
    if (selectedFolders.length === folderData.length) {
      setSelectedFolders([]);
    } else {
      setSelectedFolders(folderData.map(f => f.key));
    }
  };

  const handleObjectSelect = (objectKey) => {
    setSelectedObjects(prev => {
      if (prev.includes(objectKey)) {
        return prev.filter(o => o !== objectKey);
      } else {
        return [...prev, objectKey];
      }
    });
  };

  const handleSelectAllObjects = () => {
    const objectData = getTableData().filter(item => !item.isFolder);
    if (selectedObjects.length === objectData.length) {
      setSelectedObjects([]);
    } else {
      setSelectedObjects(objectData.map(o => o.fullKey || o.key));
    }
  };

  const handleSelectAll = () => {
    const tableData = getTableData();
    const folderData = tableData.filter(item => item.isFolder);
    const objectData = tableData.filter(item => !item.isFolder);
    
    const allFoldersSelected = folderData.length === 0 || selectedFolders.length === folderData.length;
    const allObjectsSelected = objectData.length === 0 || selectedObjects.length === objectData.length;
    const hasAnyItems = folderData.length > 0 || objectData.length > 0;
    
    // If all items are selected, deselect all. Otherwise, select all.
    if (allFoldersSelected && allObjectsSelected && hasAnyItems) {
      setSelectedFolders([]);
      setSelectedObjects([]);
    } else {
      setSelectedFolders(folderData.map(f => f.key));
      setSelectedObjects(objectData.map(o => o.fullKey || o.key));
    }
  };

  const handleDeleteSelectedObjects = async () => {
    if (selectedObjects.length === 0) {
      alert('Please select at least one file to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedObjects.length} file(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeletingMultiple(true);
      
      for (const objectKey of selectedObjects) {
        await deleteObject(objectKey);
      }
      
      setSelectedObjects([]);
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to delete some objects`);
      console.error('Error deleting objects:', err);
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  const handleDeleteSelectedFolders = async () => {
    if (selectedFolders.length === 0) {
      alert('Please select at least one folder to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedFolders.length} folder(s) and all their contents? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeletingMultiple(true);
      
      for (const folderKey of selectedFolders) {
        const folderPath = currentFolder ? `${currentFolder}${folderKey}/` : `${folderKey}/`;
        await deleteFolder(folderPath);
      }
      
      setSelectedFolders([]);
      
      // Refresh the current view
      if (currentFolder) {
        await fetchObjects(currentFolder);
      } else {
        await fetchFolders();
      }
      
      setError(null);
    } catch (err) {
      setError(`Failed to delete some folders`);
      console.error('Error deleting folders:', err);
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  const handleShareSelectedFolders = async () => {
    if (selectedFolders.length === 0) {
      alert('Please select at least one folder to share');
      return;
    }

    try {
      setGeneratingLinks(true);
      const allLinks = [];

      for (const folderKey of selectedFolders) {
        const folderPath = currentFolder ? `${currentFolder}${folderKey}/` : `${folderKey}/`;
        try {
          const result = await s3Service.generateFolderShareableLinks(folderPath);
          allLinks.push({
            folderName: folderKey,
            folderPath: folderPath,
            files: result.files,
            expiresIn: result.expiresIn
          });
        } catch (err) {
          console.error(`Error generating links for ${folderKey}:`, err);
          allLinks.push({
            folderName: folderKey,
            folderPath: folderPath,
            error: 'Failed to generate links',
            files: []
          });
        }
      }

      setShareableLinks(allLinks);
      setIsShareModalOpen(true);
    } catch (error) {
      console.error('Error generating shareable links:', error);
      alert('Failed to generate shareable links. Please try again.');
    } finally {
      setGeneratingLinks(false);
    }
  };

  const handleCopyLink = (link, index) => {
    navigator.clipboard.writeText(link)
      .then(() => {
        setLinkCopied(index);
        setTimeout(() => setLinkCopied(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        alert('Failed to copy link to clipboard');
      });
  };

  const getFileUrl = (fileKey) => {
    const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
    const region = process.env.REACT_APP_AWS_REGION || 'ap-southeast-1';
    const encodedKey = fileKey.split('/').map(encodeURIComponent).join('/');
    return `https://${bucketName}.s3.${region}.amazonaws.com/${encodedKey}`;
  };

  const handleCopyFileLink = async (fileKey) => {
    try {
      setCopyingLinkFor(fileKey);
      const url = getFileUrl(fileKey);
      await navigator.clipboard.writeText(url);
      setLinkCopied(fileKey);
      setTimeout(() => setLinkCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert('Failed to generate or copy link');
    } finally {
      setCopyingLinkFor(null);
    }
  };

  const handleCopyAllLinks = (files) => {
    const allLinks = files.map(f => `${f.fileName}: ${f.url}`).join('\n\n');
    navigator.clipboard.writeText(allLinks)
      .then(() => {
        setLinkCopied('all');
        setTimeout(() => setLinkCopied(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy links:', err);
        alert('Failed to copy links to clipboard');
      });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📁';
    const extension = fileName.split('.').pop().toLowerCase();
    const iconMap = {
      'jpg': '🖼️', 'jpeg': '🖼️', 'png': '🖼️', 'gif': '🖼️', 'svg': '🖼️',
      'pdf': '📄', 'doc': '📄', 'docx': '📄', 'txt': '📄',
      'mp4': '🎥', 'avi': '🎥', 'mov': '🎥', 'wmv': '🎥',
      'mp3': '🎵', 'wav': '🎵', 'flac': '🎵',
      'zip': '📦', 'rar': '📦', '7z': '📦',
      'js': '💻', 'html': '💻', 'css': '💻', 'json': '💻'
    };
    return iconMap[extension] || '📄';
  };

  // Prepare data for DataTable
  const getTableData = () => {
    if (currentFolder) {
      return objects;
    } else {
      // Convert folders to objects format for DataTable
      return folders.map(folder => ({
        key: folder,
        fullKey: folder + '/',
        isFolder: true,
        size: null,
        type: 'Folder'
      }));
    }
  };

  // Define columns for DataTable
  const columns = [
    {
      accessorKey: 'select',
      header: () => {
        const tableData = getTableData();
        if (tableData.length === 0) return null;
        const folderData = tableData.filter(item => item.isFolder);
        const objectData = tableData.filter(item => !item.isFolder);
        const allSelected = 
          (folderData.length === 0 || selectedFolders.length === folderData.length) &&
          (objectData.length === 0 || selectedObjects.length === objectData.length) &&
          (folderData.length > 0 || objectData.length > 0);
        return (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
        );
      },
      className: 'w-12',
      cell: (row) => {
        if (row.isFolder) {
          return (
            <input
              type="checkbox"
              checked={selectedFolders.includes(row.key)}
              onChange={() => handleFolderSelect(row.key)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
          );
        } else {
          const objectKey = row.fullKey || row.key;
          return (
            <input
              type="checkbox"
              checked={selectedObjects.includes(objectKey)}
              onChange={() => handleObjectSelect(objectKey)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
          );
        }
      }
    },
    {
      accessorKey: 'key',
      header: 'Name',
      className: 'min-w-96',
      cell: (row) => (
        <div className="flex items-center">
          <span className="mr-3 text-lg">
            {row.isFolder ? '📁' : getFileIcon(row.key)}
          </span>
          {row.isFolder ? (
            <button
              onClick={() => navigateToFolder(row.key)}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {row.key}
            </button>
          ) : (
            <span className="text-gray-900">{row.key}</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      className: 'w-32',
      cell: (row) => (
        <span className="text-sm text-gray-500">
          {row.isFolder ? 'Folder' : 'Object'}
        </span>
      )
    },
    {
      accessorKey: 'size',
      header: 'Size',
      className: 'w-32',
      cell: (row) => (
        <span className="text-sm text-gray-500">
          {formatFileSize(row.size)}
        </span>
      )
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      className: 'w-32',
      cell: (row) => (
        <div className="flex justify-center space-x-1">
          {row.isFolder ? (
            <button
              onClick={() => handleDeleteFolder(row.key)}
              disabled={deletingFolder === row.key}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete folder"
            >
              {deletingFolder === row.key ? (
                <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          ) : (
            <>
              <a
                href={getFileUrl(row.fullKey || row.key)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                title="Файл харах"
                aria-label={`Файл харах: ${row.key}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  <circle cx="12" cy="12" r="3" strokeWidth={2} />
                </svg>
              </a>
              <button
                onClick={() => handleCopyFileLink(row.fullKey || row.key)}
                disabled={copyingLinkFor === (row.fullKey || row.key)}
                className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={linkCopied === (row.fullKey || row.key) ? "Copied!" : "Copy link"}
              >
                {copyingLinkFor === (row.fullKey || row.key) ? (
                  <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                ) : linkCopied === (row.fullKey || row.key) ? (
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => handleDeleteObject(row.fullKey || row.key)}
                disabled={deletingObject === (row.fullKey || row.key)}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete object"
              >
                {deletingObject === (row.fullKey || row.key) ? (
                  <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="flex gap-3 mb-6" aria-label="File share sections">
        <Button variant={fileSection === 'general' ? 'default' : 'outline'} onClick={() => setFileSection('general')}>Ерөнхий файлууд</Button>
        <Button variant={fileSection === 'instructors' ? 'default' : 'outline'} onClick={() => setFileSection('instructors')}>Багшийн материал</Button>
      </div>
      {fileSection === 'instructors' ? <InstructorFiles user={user} /> : <>
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">File Management</h1>
            <p className="text-lg text-gray-600">Manage S3 bucket files and folders</p>
          </div>
          <div className="flex space-x-2">
            {(selectedFolders.length > 0 || selectedObjects.length > 0) && (
              <button
                onClick={() => {
                  if (selectedFolders.length > 0 && selectedObjects.length > 0) {
                    // Delete both folders and objects
                    handleDeleteSelectedFolders();
                    handleDeleteSelectedObjects();
                  } else if (selectedFolders.length > 0) {
                    handleDeleteSelectedFolders();
                  } else {
                    handleDeleteSelectedObjects();
                  }
                }}
                disabled={isDeletingMultiple}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                title="Delete selected items"
              >
                {isDeletingMultiple ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete ({selectedFolders.length + selectedObjects.length})
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleShareSelectedFolders}
              disabled={generatingLinks || selectedFolders.length === 0}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              title={selectedFolders.length === 0 ? "Select folders to share" : "Share selected folders"}
            >
              {generatingLinks ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  {selectedFolders.length > 0 ? `Share (${selectedFolders.length})` : 'Share Folders'}
                </>
              )}
            </button>
            <button
              onClick={() => setIsCreateFolderModalOpen(true)}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              📁 New Folder
            </button>
            <button
              onClick={() => setIsUploadFileModalOpen(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              disabled={loading}
            >
              📤 Upload File
            </button>
            <button
              onClick={() => setIsBulkUploadModalOpen(true)}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              disabled={loading}
            >
              📦 Bulk Upload
            </button>
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors flex items-center"
              disabled={loading || getTableData().length === 0}
            >
              {(() => {
                const tableData = getTableData();
                const folderData = tableData.filter(item => item.isFolder);
                const objectData = tableData.filter(item => !item.isFolder);
                const allSelected = 
                  (folderData.length === 0 || selectedFolders.length === folderData.length) &&
                  (objectData.length === 0 || selectedObjects.length === objectData.length) &&
                  (folderData.length > 0 || objectData.length > 0);
                return allSelected ? '☑️ Deselect All' : '☐ Select All';
              })()}
            </button>
            <button
              onClick={() => currentFolder ? fetchObjects(currentFolder) : fetchFolders()}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              🔄 Refresh
            </button>
            {currentFolder && (
              <button
                onClick={goBack}
                className="px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
              >
                ⬅️ Back
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className="mb-6 flex items-center space-x-2 text-sm text-gray-600">
          <button
            onClick={() => navigateToBreadcrumb(-1)}
            className="hover:text-blue-600 hover:underline"
          >
            Objects
          </button>
          {breadcrumb.map((folder, index) => (
            <React.Fragment key={index}>
              <span>/</span>
              <button
                onClick={() => navigateToBreadcrumb(index)}
                className="hover:text-blue-600 hover:underline"
              >
                {folder}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Objects DataTable */}
      <DataTable
        columns={columns}
        data={getTableData()}
        searchKey="key"
        searchPlaceholder="Search files and folders..."
        loading={loading}
        emptyMessage={
          currentFolder 
            ? "No objects found in this folder."
            : "No folders found. Upload some files to see them here."
        }
        pageSize={20}
      />

      {/* Create Folder Modal */}
      <Modal 
        isOpen={isCreateFolderModalOpen} 
        onClose={() => {
          setIsCreateFolderModalOpen(false);
          setNewFolderName('');
        }}
        className="max-w-md"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Create New Folder</h2>
          <p className="text-sm text-gray-600 mt-1">
            {currentFolder ? `Creating in: ${currentFolder}` : 'Creating in root'}
          </p>
        </ModalHeader>
        
        <ModalBody>
          <div>
            <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 mb-2">
              Folder Name
            </label>
            <Input
              id="folderName"
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              disabled={isCreatingFolder}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder();
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Folder names should not contain special characters or spaces
            </p>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button
            onClick={() => {
              setIsCreateFolderModalOpen(false);
              setNewFolderName('');
            }}
            variant="outline"
            disabled={isCreatingFolder}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            disabled={isCreatingFolder || !newFolderName.trim()}
            className="bg-green-600 hover:bg-green-700"
          >
            {isCreatingFolder ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                Creating...
              </>
            ) : (
              'Create Folder'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Upload File Modal */}
      <Modal 
        isOpen={isUploadFileModalOpen} 
        onClose={() => {
          setIsUploadFileModalOpen(false);
          setSelectedFile(null);
        }}
        className="max-w-md"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Upload File</h2>
          <p className="text-sm text-gray-600 mt-1">
            {currentFolder ? `Uploading to: ${currentFolder}` : 'Uploading to root'}
          </p>
        </ModalHeader>
        
        <ModalBody>
          <div>
            <label htmlFor="fileInput" className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <input
              id="fileInput"
              type="file"
              onChange={handleFileSelect}
              disabled={isUploadingFile}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {selectedFile && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-900">
                  <span className="font-medium">Selected:</span> {selectedFile.name}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              All file types are supported
            </p>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button
            onClick={() => {
              setIsUploadFileModalOpen(false);
              setSelectedFile(null);
            }}  
            variant="outline"
            disabled={isUploadingFile}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadFile}
            disabled={isUploadingFile || !selectedFile}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isUploadingFile ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                Uploading...
              </>
            ) : (
              'Upload File'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal 
        isOpen={isBulkUploadModalOpen} 
        onClose={() => {
          if (!isBulkUploading) {
            setIsBulkUploadModalOpen(false);
            setSelectedFiles([]);
            setBulkUploadProgress(null);
            if (bulkFileInputRef.current) {
              bulkFileInputRef.current.value = '';
            }
          } 
        }}
        className="max-w-lg"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Bulk Upload Files</h2>
          <p className="text-sm text-gray-600 mt-1">
            {currentFolder ? `Uploading to: ${currentFolder}` : 'Uploading to root'}
          </p>
        </ModalHeader>
        
        <ModalBody>
          <div>
            <label htmlFor="bulkFileInput" className="block text-sm font-medium text-gray-700 mb-2">
              Select Multiple Files
            </label>
            <input
              id="bulkFileInput"
              ref={bulkFileInputRef}
              type="file"
              multiple
              onChange={handleBulkFileSelect}
              disabled={isBulkUploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {selectedFiles.length > 0 && (
              <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded max-h-48 overflow-y-auto">
                <p className="text-sm text-orange-900 font-medium mb-2">
                  Selected {selectedFiles.length} file(s):
                </p>
                <ul className="space-y-1">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="text-xs text-orange-700 flex justify-between">
                      <span className="truncate mr-2">{file.name}</span>
                      <span className="whitespace-nowrap">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-orange-600 mt-2 pt-2 border-t border-orange-200">
                  Total size: {(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
            {bulkUploadProgress && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="flex justify-between text-sm text-blue-900 mb-2">
                  <span>Uploading: {bulkUploadProgress.currentFile}</span>
                  <span>{bulkUploadProgress.completed}/{bulkUploadProgress.total}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${bulkUploadProgress.percentage}%` }}
                  ></div>
                </div>
                <p className="text-xs text-blue-700 mt-1 text-center">
                  {bulkUploadProgress.percentage}% complete
                </p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Select multiple files by holding Ctrl/Cmd while clicking
            </p>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button
            onClick={() => {
              setIsBulkUploadModalOpen(false);
              setSelectedFiles([]);
              setBulkUploadProgress(null);
              if (bulkFileInputRef.current) {
                bulkFileInputRef.current.value = '';
              }
            }}
            variant="outline"
            disabled={isBulkUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpload}
            disabled={isBulkUploading || selectedFiles.length === 0}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {isBulkUploading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block"></div>
                Uploading...
              </>
            ) : (
              `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Share Links Modal */}
      <Modal 
        isOpen={isShareModalOpen} 
        onClose={() => {
          setIsShareModalOpen(false);
          setShareableLinks([]);
          setSelectedFolders([]);
          setLinkCopied(null);
        }}
        className="max-w-4xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">Shareable Folder Links</h2>
          <p className="text-sm text-gray-600 mt-1">
            Generated links for {shareableLinks.length} folder{shareableLinks.length !== 1 ? 's' : ''} (Valid for 7 days)
          </p>
        </ModalHeader>
        
        <ModalBody>
          <div className="space-y-6 max-h-96 overflow-y-auto">
            {shareableLinks.map((folderData, folderIndex) => (
              <div key={folderIndex} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">📁</span>
                    <h3 className="font-semibold text-gray-900">{folderData.folderName}</h3>
                  </div>
                  {folderData.files && folderData.files.length > 0 && (
                    <Button
                      onClick={() => handleCopyAllLinks(folderData.files)}
                      className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700"
                    >
                      {linkCopied === 'all' ? '✓ Copied!' : 'Copy All'}
                    </Button>
                  )}
                </div>
                
                {folderData.error ? (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {folderData.error}
                  </div>
                ) : folderData.files && folderData.files.length > 0 ? (
                  <div className="space-y-2">
                    {folderData.files.map((file, fileIndex) => (
                      <div key={fileIndex} className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <span>{getFileIcon(file.fileName)}</span>
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {file.fileName}
                            </span>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={file.url}
                            readOnly
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white font-mono"
                            onClick={(e) => e.target.select()}
                          />
                          <Button
                            onClick={() => handleCopyLink(file.url, `${folderIndex}-${fileIndex}`)}
                            className="text-xs bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                          >
                            {linkCopied === `${folderIndex}-${fileIndex}` ? '✓ Copied!' : 'Copy'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                    No files found in this folder
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">About these links:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Links expire in 7 days from now</li>
                  <li>Anyone with these links can download the files</li>
                  <li>No authentication required</li>
                  <li>Share these links securely</li>
                </ul>
              </div>
            </div>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button
            onClick={() => {
              setIsShareModalOpen(false);
              setShareableLinks([]);
              setSelectedFolders([]);
              setLinkCopied(null);
            }}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      </>}
    </div>
  );
};

export default FileManagement;
